import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Download, Eye } from "lucide-react";
import { applicationStatusLabel } from "../../utils/applicationStatus";
import { applicationNextStep } from "../../utils/applicationNextStep";
import {
  VisaApi,
  VisaWorkspace,
  VisaFile,
  VisaRequest,
} from "./visaSupportApi";
import "./visaSupport.css";
export const VISA_INTRO =
  "Get support from the Visa Support Team throughout your student’s visa application process. Upload the student’s visa documents for review and receive guidance on next steps here.";
const progress: Record<string, string> = {
  NOT_STARTED: "Not started",
  AWAITING_REVIEW: "Awaiting review",
  AGENT_ACTION_REQUIRED: "Agent action required",
  GUIDANCE_PROVIDED: "Guidance provided",
};
const errorText = (e: any) =>
  e?.response?.data?.error ||
  e?.message ||
  "Unable to load Visa Support. Please retry.";
export function VisaSupportWorkspace(props: {
  applicationId: number;
  accountKey: string;
  api: VisaApi;
}) {
  return (
    <VisaSupportSession
      key={`${props.accountKey}:${props.applicationId}`}
      {...props}
    />
  );
}
function VisaSupportSession({
  applicationId,
  accountKey,
  api,
}: {
  applicationId: number;
  accountKey: string;
  api: VisaApi;
}) {
  const [data, setData] = useState<VisaWorkspace | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [requestPage, setRequestPage] = useState(1),
    [responsePage, setResponsePage] = useState(1),
    [historyCursor, setHistoryCursor] = useState<number | undefined>(),
    [historyBusy, setHistoryBusy] = useState(false),
    [older, setOlder] = useState<VisaWorkspace["messages"]>([]);
  const [text, setText] = useState(""),
    [files, setFiles] = useState<File[]>([]),
    [response, setResponse] = useState(""),
    [required, setRequired] = useState(false),
    [labels, setLabels] = useState("");
  const dialog = useRef<HTMLDivElement>(null);
  const latestWindow = useRef<VisaWorkspace["messages"]>([]);
  const historyLock = useRef(false),
    historyController = useRef<AbortController>();
  const operation = useRef(""),
    lock = useRef(false),
    epoch = useRef(0),
    controller = useRef<AbortController>(),
    [preview, setPreview] = useState<{
      url: string;
      type: string;
      name: string;
    } | null>(null);
  const load = useCallback(async () => {
    controller.current?.abort();
    const control = new AbortController();
    controller.current = control;
    const version = epoch.current;
    try {
      const result = await api.read(
        applicationId,
        { requestPage, responsePage },
        control.signal,
      );
      if (!control.signal.aborted && version === epoch.current) {
        const lastResponsePage = Math.max(
          1,
          Math.ceil(
            result.pagination.pendingResponseCount / result.pagination.pageSize,
          ),
        );
        const lastRequestPage = Math.max(
          1,
          Math.ceil(
            result.pagination.requestCount / result.pagination.pageSize,
          ),
        );
        if (responsePage > lastResponsePage || requestPage > lastRequestPage) {
          setResponse("");
          operation.current = "";
          if (responsePage > lastResponsePage)
            setResponsePage(lastResponsePage);
          if (requestPage > lastRequestPage) setRequestPage(lastRequestPage);
          return;
        }
        const previousWindow = latestWindow.current;
        setOlder((previous) =>
          previous.length
            ? Array.from(
                new Map(
                  [...previous, ...previousWindow].map((m) => [m.id, m]),
                ).values(),
              )
            : previous,
        );
        latestWindow.current = result.messages;
        setData(result);
        setResponse((previous) =>
          result.pendingResponses.some((r) => String(r.id) === previous)
            ? previous
            : "",
        );
        setError("");
      }
    } catch (e) {
      if (!control.signal.aborted && version === epoch.current)
        setError(errorText(e));
    }
  }, [api, applicationId, requestPage, responsePage]);
  useEffect(() => {
    epoch.current++;
    setData(null);
    setError("");
    setText("");
    setFiles([]);
    setResponse("");
    setLabels("");
    setRequired(false);
    operation.current = "";
    setRequestPage(1);
    setResponsePage(1);
    setHistoryCursor(undefined);
    setHistoryBusy(false);
    setPreview(null);
    setBusy(false);
    setOlder([]);
    return () => {
      epoch.current++;
      controller.current?.abort();
      historyController.current?.abort();
    };
  }, [applicationId, accountKey]);
  useEffect(() => {
    void load();
    const focus = () => {
      if (!document.hidden) void load();
    };
    window.addEventListener("focus", focus);
    document.addEventListener("visibilitychange", focus);
    const timer = setInterval(focus, 60000);
    return () => {
      controller.current?.abort();
      clearInterval(timer);
      window.removeEventListener("focus", focus);
      document.removeEventListener("visibilitychange", focus);
    };
  }, [load, accountKey]);
  useEffect(() => {
    if (!preview) return;
    const previous = document.activeElement as HTMLElement | null;
    const box = dialog.current;
    const first = box?.querySelector<HTMLElement>("button");
    first?.focus();
    const keys = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setPreview(null);
      }
      if (event.key === "Tab") {
        const nodes = Array.from(
          box?.querySelectorAll<HTMLElement>('button,iframe,[tabindex="0"]') ||
            [],
        );
        if (nodes.length) {
          const start = nodes[0],
            end = nodes[nodes.length - 1];
          if (event.shiftKey && document.activeElement === start) {
            event.preventDefault();
            end.focus();
          } else if (!event.shiftKey && document.activeElement === end) {
            event.preventDefault();
            start.focus();
          }
        }
      }
    };
    window.addEventListener("keydown", keys);
    return () => {
      window.removeEventListener("keydown", keys);
      if (previous?.isConnected) previous.focus();
    };
  }, [preview]);
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview.url);
    },
    [preview],
  );
  const mutate = async (action: () => Promise<unknown>, clear = false) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setError("");
    const version = epoch.current;
    try {
      await action();
      if (version === epoch.current) {
        if (clear) {
          setText("");
          setFiles([]);
          setResponse("");
          setLabels("");
          setRequired(false);
          operation.current = "";
        }
        await load();
      }
    } catch (e) {
      if (version === epoch.current) setError(errorText(e));
    } finally {
      lock.current = false;
      if (version === epoch.current) setBusy(false);
    }
  };
  const changed = () => {
    operation.current = "";
  };
  const send = (event: FormEvent) => {
    event.preventDefault();
    if (
      files.length > 10 ||
      files.some((f) => f.size > 10 * 1024 * 1024) ||
      files.reduce((n, f) => n + f.size, 0) > 20 * 1024 * 1024
    ) {
      setError(
        "Choose at most 10 files, each at most 10 MB; combined maximum 20 MB.",
      );
      return;
    }
    const body = new FormData();
    body.append("operationId", (operation.current ||= crypto.randomUUID()));
    body.append("text", text);
    body.append("responseRequired", String(required));
    if (response) body.append("respondsToId", response);
    body.append(
      "documentRequests",
      JSON.stringify(
        labels
          .split("\n")
          .map((label) => label.trim())
          .filter(Boolean)
          .map((label) => ({ label, category: "visa" })),
      ),
    );
    files.forEach((file) => body.append("files", file));
    void mutate(() => api.send(applicationId, body), true);
  };
  const olderHistory = async () => {
    if (historyLock.current || !data) return;
    historyLock.current = true;
    const control = new AbortController();
    historyController.current = control;
    setHistoryBusy(true);
    const version = epoch.current;
    try {
      const result = await api.read(
        applicationId,
        {
          beforeId: historyCursor || data.nextBeforeId!,
          requestPage,
          responsePage,
        },
        control.signal,
      );
      if (!control.signal.aborted && version === epoch.current) {
        setOlder((previous) => [
          ...result.messages,
          ...previous,
          ...latestWindow.current,
        ]);
        setHistoryCursor(result.nextBeforeId || 0);
      }
    } catch (e) {
      if (version === epoch.current) setError(errorText(e));
    } finally {
      historyLock.current = false;
      if (version === epoch.current) setHistoryBusy(false);
    }
  };
  const openFile = async (file: VisaFile, download = false) => {
    const version = epoch.current;
    try {
      const blob = await api.file(applicationId, file);
      if (version !== epoch.current) return;
      const url = URL.createObjectURL(blob);
      if (download) {
        const a = document.createElement("a");
        a.href = url;
        a.download = file.originalName;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } else setPreview({ url, type: file.mimeType, name: file.originalName });
    } catch (e) {
      if (version === epoch.current) setError(errorText(e));
    }
  };
  const fileActions = (file: VisaFile) => (
    <div className="d-flex gap-1 flex-shrink-0">
      <button
        type="button"
        className="btn btn-sm btn-outline-primary"
        title="Preview"
        aria-label={`Preview ${file.originalName}`}
        onClick={() => void openFile(file)}
      >
        <Eye size={14} />
      </button>
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        title="Download"
        aria-label={`Download ${file.originalName}`}
        onClick={() => void openFile(file, true)}
      >
        <Download size={14} />
      </button>
    </div>
  );
  if (!data)
    return (
      <section className="card p-4" aria-label="Visa Support">
        {error ? (
          <div role="alert">
            {error}{" "}
            <button
              className="btn btn-outline-primary"
              onClick={() => void load()}
            >
              Retry
            </button>
          </div>
        ) : (
          <p role="status">Loading Visa Support workspace…</p>
        )}
      </section>
    );
  const guidance = applicationNextStep(data.application.status);
  const agentActionRequired = data.viewerRole === "AGENT" &&
    data.pendingRequestCount + data.pendingResponseCount > 0;
  return (
    <section className="visa-support" aria-label="Visa Support">
      <div className="card p-4 mb-4">
        <div className="d-flex flex-wrap justify-content-between gap-3">
          <div>
            <h2 className="h4 fw-bold">Visa Support</h2>
            <p className="mb-1 fw-semibold">
              {data.application.studentName} · {data.application.reference}
            </p>
            <p className="text-muted mb-3">{data.application.institution}</p>
          </div>
          <div>
            <span className="badge bg-light text-dark border">
              {progress[data.progress] || data.progress}
            </span>
            <p className="small mt-2 mb-0">
              Application stage:{" "}
              {applicationStatusLabel(data.application.status)}
            </p>
            {!["VISA_APPLICATION", "VISA_APPROVED"].includes(
              data.application.status,
            ) && <p className="small text-muted">Visa stage not recorded</p>}
          </div>
        </div>
        <p className="text-muted mb-2">{VISA_INTRO}</p>
        {guidance.kind !== "unknown" && (
          <p className="small mb-0">Application next step: {guidance.text}</p>
        )}
      </div>
      {error && (
        <div className="alert alert-warning" role="alert">
          {error} Previously loaded information may be out of date.{" "}
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => void load()}
          >
            Retry
          </button>
        </div>
      )}
      {!data.canHandle && (
        <div className="alert alert-secondary">
          {data.viewerRole === "ADMIN"
            ? "Read-only. The designated responsible administrator handles review. Use Application responsibility to claim an eligible application or request reassignment."
            : "Updates are temporarily unavailable while this application is being handled. Please refresh shortly."}
        </div>
      )}
      {!data.case ? (
        <div className="card p-4">
          <h3 className="h5">Start Visa Support</h3>
          <p>
            No support case has been started. Starting a case does not change
            the application stage.
          </p>
          <button
            className="btn btn-primary align-self-start"
            disabled={!data.canHandle || busy}
            onClick={() => void mutate(() => api.start(applicationId))}
          >
            {busy ? "Starting…" : "Start Visa Support"}
          </button>
        </div>
      ) : (
        <>
          <div className="row g-4">
            <div className="col-lg-8">
              <div className="card p-4 mb-4">
                <h3 className="h5">Latest guidance</h3>
                {data.latestGuidance ? (
                  <>
                    <p className="visa-text">{data.latestGuidance.text}</p>
                    <small className="text-muted">
                      {data.latestGuidance.authorName} ·{" "}
                      {new Date(data.latestGuidance.createdAt).toLocaleString()}
                    </small>
                    {data.latestGuidance.attachments.map((a) => (
                      <div key={a.fileId} className="visa-file">
                        <span>{a.file.originalName}</span>
                        {fileActions(a.file)}
                      </div>
                    ))}
                  </>
                ) : (
                  <p className="text-muted mb-0">No staff guidance yet.</p>
                )}
              </div>
              <div className="card p-4 mb-4">
                <h3 className="h5">
                  Response history{" "}
                  <small className="text-muted">({data.messageCount})</small>
                </h3>
                {(
                  historyCursor === undefined
                    ? data.nextBeforeId
                    : historyCursor
                ) ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary mb-3"
                    disabled={historyBusy}
                    onClick={() => void olderHistory()}
                  >
                    Load earlier history
                  </button>
                ) : null}
                {Array.from(
                  new Map(
                    [...older, ...data.messages].map((m) => [m.id, m]),
                  ).values(),
                )
                  .sort((a, b) => a.id - b.id)
                  .map((message) => (
                    <article key={message.id} className="border-bottom py-3">
                      <div className="d-flex flex-wrap justify-content-between gap-2">
                        <strong>
                          {message.authorName}{" "}
                          <span className="small text-muted fw-normal">
                            {message.authorRole === "ADMIN"
                              ? "Visa Support Team"
                              : "Agent"}
                          </span>
                        </strong>
                        <time className="small text-muted">
                          {new Date(message.createdAt).toLocaleString()}
                        </time>
                      </div>
                      <p className="visa-text mt-2">{message.text}</p>
                      {message.respondsToId && (
                        <p className="small text-muted">
                          Response to guidance #{message.respondsToId}
                        </p>
                      )}
                      {message.attachments.map((a) => (
                        <div key={a.fileId} className="visa-file">
                          <span>{a.file.originalName}</span>
                          {fileActions(a.file)}
                        </div>
                      ))}
                    </article>
                  ))}
                {!data.messageCount && (
                  <p className="text-muted">
                    No messages yet. Upload documents with a message to begin
                    review.
                  </p>
                )}
              </div>
              <form className="card p-4" onSubmit={send}>
                <h3 className="h5">
                  {data.viewerRole === "ADMIN"
                    ? "Provide guidance"
                    : "Message the Visa Support Team"}
                </h3>
                {data.viewerRole === "AGENT" && (
                  <>
                    <label className="form-label" htmlFor="visa-response">
                      Responding to
                    </label>
                    <select
                      id="visa-response"
                      className="form-select mb-3"
                      value={response}
                      disabled={busy || !data.canHandle}
                      onChange={(e) => {
                        setResponse(e.target.value);
                        changed();
                      }}
                    >
                      <option value="">
                        New message (does not complete a required response)
                      </option>
                      {data.pendingResponses.map((r) => (
                        <option value={r.id} key={r.id}>
                          #{r.id} · {r.text.slice(0, 100)}
                        </option>
                      ))}
                    </select>
                  </>
                )}
                <label htmlFor="visa-message" className="form-label">
                  Message
                </label>
                <textarea
                  id="visa-message"
                  className="form-control mb-3"
                  required
                  maxLength={10000}
                  rows={4}
                  value={text}
                  disabled={busy || !data.canHandle}
                  onChange={(e) => {
                    setText(e.target.value);
                    changed();
                  }}
                />
                {data.viewerRole === "ADMIN" && (
                  <>
                    <label className="form-check mb-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={required}
                        disabled={busy || !data.canHandle}
                        onChange={(e) => {
                          setRequired(e.target.checked);
                          changed();
                        }}
                      />
                      Require a written response
                    </label>
                    <label htmlFor="visa-requests" className="form-label">
                      Request documents (one named document per line)
                    </label>
                    <textarea
                      id="visa-requests"
                      className="form-control mb-3"
                      value={labels}
                      disabled={busy || !data.canHandle}
                      onChange={(e) => {
                        setLabels(e.target.value);
                        changed();
                      }}
                    />
                  </>
                )}
                <label className="form-label" htmlFor="visa-files">
                  Attachments
                </label>
                <input
                  key={files.length ? "selected" : "empty"}
                  id="visa-files"
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  className="form-control"
                  disabled={busy || !data.canHandle}
                  onChange={(e) => {
                    setFiles(Array.from(e.target.files || []));
                    changed();
                  }}
                />
                <p className="small text-muted mt-2">
                  PDF, JPG, PNG, DOC or DOCX. Up to 10 files, 10 MB each; 20 MB
                  combined.
                </p>
                {files.map((f, i) => (
                  <div className="small visa-text" key={i}>
                    {f.name}
                  </div>
                ))}
                <button
                  className="btn btn-primary mt-3 align-self-start"
                  disabled={busy || !data.canHandle || !text.trim()}
                >
                  {busy ? "Sending…" : "Send message"}
                </button>
              </form>
            </div>
            <div className="col-lg-4">
              <div className={`card p-4${agentActionRequired ? " visa-actions--pending" : ""}`}>
                <h3 className="h5">Outstanding actions</h3>
                {agentActionRequired && (
                  <p className="visa-actions__notice" role="status">
                    Action required — please complete the pending items below.
                  </p>
                )}
                <p className="small text-muted">
                  {data.pendingRequestCount} {data.pendingRequestCount === 1 ? "document" : "documents"} ·{" "}
                  {data.pendingResponseCount} {data.pendingResponseCount === 1 ? "written response" : "written responses"}
                </p>
                {data.pendingResponses.map((r) => (
                  <div className="border rounded p-3 mb-3" key={r.id}>
                    <strong className="small">
                      Written response required · #{r.id}
                    </strong>
                    <p className="small visa-text mb-0">{r.text}</p>
                  </div>
                ))}
                <Pager
                  page={responsePage}
                  total={data.pagination.pendingResponseCount}
                  size={20}
                  change={(page) => {
                    setResponse("");
                    operation.current = "";
                    setResponsePage(page);
                  }}
                />
                {data.documentRequests.map((r) => (
                  <div className="border rounded p-3 mb-3" key={r.id}>
                    <strong className="visa-text">{r.label}</strong>
                    <p className="small text-muted">
                      {r.fulfilledDocumentId ? "Supplied" : "Document required"}
                      {r.fulfilledAt &&
                        ` · ${new Date(r.fulfilledAt).toLocaleString()}`}
                      {r.suppliedByName && ` by ${r.suppliedByName}`}
                      {!r.fulfilledDocumentId &&
                        r.requestedByName &&
                        ` · ${r.requestedByName}`}
                    </p>
                    {r.fulfilledDocument ? (
                      <>
                        <div className="small visa-text mb-2">
                          {r.fulfilledDocument.originalName}
                        </div>
                        {fileActions(r.fulfilledDocument)}
                      </>
                    ) : (
                      data.viewerRole === "AGENT" &&
                      api.supply && (
                        <Supply
                          key={`${accountKey}-${applicationId}-${r.id}`}
                          request={r}
                          disabled={busy || !data.canHandle}
                          upload={(f, op) =>
                            api.supply!(applicationId, r, f, op)
                          }
                          refresh={load}
                        />
                      )
                    )}
                  </div>
                ))}
                <Pager
                  page={requestPage}
                  total={data.pagination.requestCount}
                  size={20}
                  change={setRequestPage}
                />
                {!data.pendingRequestCount && !data.pendingResponseCount && (
                  <p className="text-muted small">
                    No outstanding agent actions.
                  </p>
                )}
                <p className="small text-muted mb-0">
                  Supplying documents does not indicate visa approval.
                </p>
              </div>
            </div>
          </div>
        </>
      )}
      {preview && (
        <div
          ref={dialog}
          className="visa-preview"
          role="dialog"
          aria-modal="true"
          aria-label={`Preview ${preview.name}`}
        >
          <div className="bg-white p-3 rounded w-100">
            <div className="d-flex justify-content-between gap-3 mb-3">
              <strong className="visa-text">{preview.name}</strong>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setPreview(null)}
              >
                Close preview
              </button>
            </div>
            {preview.type.startsWith("image/") ? (
              <img
                src={preview.url}
                alt={preview.name}
                style={{ maxWidth: "100%", maxHeight: "75vh" }}
              />
            ) : preview.type === "application/pdf" ? (
              <iframe
                title={preview.name}
                src={preview.url}
                style={{ width: "100%", height: "75vh" }}
              />
            ) : (
              <p>Preview is unavailable for this format. Download to view.</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
function Pager({
  page,
  total,
  size,
  change,
}: {
  page: number;
  total: number;
  size: number;
  change: (n: number) => void;
}) {
  return total > size ? (
    <div className="d-flex gap-2 align-items-center my-2">
      <button
        className="btn btn-sm btn-outline-secondary"
        disabled={page <= 1}
        onClick={() => change(page - 1)}
        type="button"
      >
        Previous
      </button>
      <span className="small">
        Page {page} of {Math.ceil(total / size)}
      </span>
      <button
        className="btn btn-sm btn-outline-secondary"
        disabled={page * size >= total}
        onClick={() => change(page + 1)}
        type="button"
      >
        Next
      </button>
    </div>
  ) : null;
}
function Supply({
  request,
  disabled,
  upload,
  refresh,
}: {
  request: VisaRequest;
  disabled: boolean;
  upload: (f: File, op: string) => Promise<unknown>;
  refresh: () => Promise<void>;
}) {
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const [file, setFile] = useState<File | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    op = useRef(""),
    lock = useRef(false);
  return (
    <div>
      <label htmlFor={`visa-file-${request.id}`} className="form-label small">
        File for {request.label}
      </label>
      <input
        id={`visa-file-${request.id}`}
        type="file"
        className="form-control form-control-sm"
        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
        disabled={disabled || busy}
        onChange={(e) => {
          setFile(e.target.files?.[0] || null);
          op.current = "";
          setError("");
        }}
      />
      {error && (
        <p role="alert" className="small text-danger">
          {error}
        </p>
      )}
      <button
        type="button"
        className="btn btn-sm btn-primary mt-2"
        disabled={disabled || busy || !file}
        onClick={async () => {
          if (!file || lock.current) return;
          if (file.size > 10 * 1024 * 1024) {
            setError("File must be 10 MB or smaller");
            return;
          }
          lock.current = true;
          setBusy(true);
          try {
            await upload(file, (op.current ||= crypto.randomUUID()));
            if (alive.current) await refresh();
          } catch (e) {
            if (alive.current) setError(errorText(e));
          } finally {
            lock.current = false;
            if (alive.current) setBusy(false);
          }
        }}
      >
        {busy ? "Uploading…" : "Supply document"}
      </button>
    </div>
  );
}
